import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  message,
} from 'antd';
import { SettingOutlined } from '@ant-design/icons';

import OnTraccrButton from '../../../common/buttons/OnTraccrButton';

import FormSelector from '../../FormWorkflows/selectors/FormSelector';
import UserAssignmentSelector from '../../FormWorkflows/selectors/UserAssignmentSelector';

import { createTargetToSourceMap, createSourceToTargetMap } from '../../formHelpers';
import BorderlessButton from '../../../common/buttons/BorderlessButton';
import WorkflowTriggerConfigureDrawer from '../../FormWorkflows/WorkflowTriggerConfigureDrawer';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';

const AUTHOR_HELP = 'The new form will be assigned to you';

function FieldTriggerPopover({
  sections,
  projectId,
  templateId,
  divisions,
  setConfigProps,
  configProps,
  responding = false,
  name,
  onFieldTrigger,
  setShowPopover,
}) {
  const [divisionId] = divisions;

  const {
    fieldTriggerProps: {
      form: initialForm,
      users: initialUsers,
      fieldMappings: initialFieldMappings = {},
      sharedForm: initialSharedForm,
      editable = false,
    } = {},
  } = configProps;

  const locked = !editable && responding;

  const [messageApi, contextHolder] = message.useMessage();

  const [selectedForm, setSelectedForm] = useState(initialForm);
  const [selectedUsers, setSelectedUsers] = useState(initialUsers || []);
  const [sharedForm, setSharedForm] = useState(initialSharedForm);
  const [fieldMappings, setFieldMappings] = useState(
    initialFieldMappings?.version === 'v2' ? initialFieldMappings : createTargetToSourceMap(initialFieldMappings),
  );

  const [loading, setLoading] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const openDrawer = useCallback(() => setShowDrawer(true), []);
  const closeDrawer = useCallback(() => setShowDrawer(false), []);

  const onFormChange = useCallback((newForm) => {
    setSelectedForm(newForm);
    if (!setConfigProps) return; // switching preview & responding settings wont affect config
    setConfigProps({
      ...configProps,
      fieldTriggerProps: {
        ...configProps.fieldTriggerProps,
        form: newForm,
      },
    });
  }, [configProps]);

  const onRecipientChange = useCallback((newUsers) => {
    setSelectedUsers(newUsers);
    if (!setConfigProps) return;
    setConfigProps({
      ...configProps,
      fieldTriggerProps: {
        ...configProps.fieldTriggerProps,
        users: newUsers,
      },
    });
  }, [configProps]);

  const onSharedChange = useCallback((checked) => {
    setSharedForm(checked);
    if (!setConfigProps) return;
    setConfigProps({
      ...configProps,
      fieldTriggerProps: {
        ...configProps.fieldTriggerProps,
        sharedForm: checked,
      },
    });
  }, [configProps]);

  const onFieldMappingsChange = useCallback((newMappings, formId) => {
    const updatedFieldMappings = {
      ...fieldMappings,
      version: 'v2',
      [formId]: createSourceToTargetMap(newMappings),
    };
    setFieldMappings(updatedFieldMappings);
    if (!setConfigProps) return;
    setConfigProps({
      ...configProps,
      fieldTriggerProps: {
        ...configProps.fieldTriggerProps,
        fieldMappings: updatedFieldMappings,
      },
    });
  }, [fieldMappings, configProps]);

  const onTrigger = useCallback(async () => {
    setLoading(true);
    if (await onFieldTrigger({
      form: selectedForm,
      users: selectedUsers,
      fieldMappings,
      sharedForm,
    })) {
      messageApi.success('Form has been triggered');
    } else {
      messageApi.error('Failed to trigger form');
    }
    setLoading(false);
    setShowPopover(false);
  }, [
    selectedForm,
    selectedUsers,
    sharedForm,
    onFieldTrigger,
  ]);

  useEffect(() => {
    // clears new fields in form template configure
    setSelectedForm(initialForm);
    setSelectedUsers(initialUsers || []);
    setSharedForm(initialSharedForm);
    setFieldMappings(initialFieldMappings?.version === 'v2' ? initialFieldMappings : createTargetToSourceMap(initialFieldMappings));
  }, [configProps]);

  return (
    <div style={{ width: 200 }}>
      {contextHolder}
      {!responding && (
        <OnTraccrCheckbox
          onChange={onSharedChange}
          defaultChecked={initialSharedForm}
          value={sharedForm}
          label="Shared Form?"
          hoverNode={(
            <div style={{ width: 300 }}>
              Check this box if you want to create one form and assign it to multiple people
              <br />
              <br />
              Leave this box unchecked if you want to create a separate form for each person.
            </div>
          )}
        />
      )}
      <FormSelector
        divisionId={divisionId}
        templateId={templateId}
        projectId={projectId}
        hideExternalForms
        onChange={onFormChange}
        selected={selectedForm}
        disable={locked}
      />
      <UserAssignmentSelector
        type="user"
        divisions={[divisionId]}
        showPositions
        showFields
        showFormAuthor
        sections={sections}
        onChange={onRecipientChange}
        selected={selectedUsers}
        disabled={locked}
        formAuthorHelperText={AUTHOR_HELP}
      />
      {locked && (
        <Row>
          <b>Options cannot be changed for this field.</b>
        </Row>
      )}
      {responding && (
        <Row justify="end">
          <OnTraccrButton
            title="Trigger"
            onClick={onTrigger}
            disabled={!selectedForm || selectedUsers.length === 0 || loading}
            style={{
              marginTop: '10px',
            }}
            loading={loading}
          />
        </Row>
      )}
      {!responding && (
        <>
          <BorderlessButton
            title="Configure"
            style={{ margin: 5 }}
            iconNode={<SettingOutlined />}
            onClick={openDrawer}
            disabled={!selectedForm}
          />
          <WorkflowTriggerConfigureDrawer
            visible={showDrawer}
            onClose={closeDrawer}
            onSubmit={closeDrawer}
            targetFormId={selectedForm}
            sourceName={name}
            sourceSections={sections}
            fieldMappings={fieldMappings}
            onFieldMappingsChange={onFieldMappingsChange}
            divisionId={divisionId}
            templateId={templateId}
            zIndex={1031} // popover z-index is 1030
          />
        </>
      )}
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
FieldTriggerPopover.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  projectId: PropTypes.string,
  templateId: PropTypes.string,
  divisions: PropTypes.arrayOf(PropTypes.string),
  configProps: PropTypes.object,
  setConfigProps: PropTypes.func,
  responding: PropTypes.bool,
  name: PropTypes.string,
  onFieldTrigger: PropTypes.func,
  setShowPopover: PropTypes.func.isRequired,
};

FieldTriggerPopover.defaultProps = {
  sections: [],
  projectId: null,
  templateId: null,
  divisions: [],
  configProps: {},
  responding: false,
  setConfigProps: null,
  name: null,
  onFieldTrigger: null,
};

export default FieldTriggerPopover;
